import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAnalysisStore } from '../../store/analysisStore';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import AssessmentChart from './AssessmentChart';
import AssessmentComparison from './AssessmentComparison';
import { computeScores } from './computeScores';

export default function DashboardView() {
  const { sourceId } = useParams<{ sourceId: string }>();
  const navigate = useNavigate();
  const { 
    sourceHistory, 
    fetchSourceHistory, 
    assessmentLoading,
    removeAssessment,
    removeSourceAssessments,
    firebaseUser
  } = useAnalysisStore();

  useEffect(() => {
    if (sourceId) {
      fetchSourceHistory(sourceId);
    }
  }, [sourceId, fetchSourceHistory]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  if (!firebaseUser || firebaseUser.isAnonymous) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">You must be logged in to view source dashboards.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Back to Search
        </Button>
      </Container>
    );
  }

  const url = sourceHistory.length > 0 ? (sourceHistory[0].url || sourceHistory[0].input_text || 'Unknown') : 'Unknown Source';

  if (!sourceId) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">No Source ID provided.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Back to Search
        </Button>
      </Container>
    );
  }

  if (assessmentLoading && sourceHistory.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
          >
            Trust Index
          </Link>
          <Typography color="text.primary">Source Dashboard</Typography>
        </Breadcrumbs>
        
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Source Analysis Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {url}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, flexShrink: 0 }}
          >
            Back to Search
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Box display="flex" flexDirection="column" gap={4}>
        {/* Comparison Section */}
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>Model Comparison</Typography>
          <AssessmentComparison data={sourceHistory} />
        </Paper>

        {/* Analytics Section */}
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>Historical Trends</Typography>
          <AssessmentChart data={sourceHistory} />
        </Paper>

        {/* Detailed History Table */}
        <Box>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={1}
            mb={2}
          >
            <Typography variant="h5" fontWeight="bold">
              Assessment Records
            </Typography>
            {sourceHistory.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={async () => {
                  if (window.confirm('Permanently delete all historical records for this source? This cannot be undone.')) {
                    await removeSourceAssessments(sourceId!);
                    navigate('/');
                  }
                }}
              >
                Clear All Source History
              </Button>
            )}
          </Box>
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <Table aria-label="assessments table" sx={{ minWidth: 600 }}>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>LLM Provider</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Score</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Rating</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sourceHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No records found for this source.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sourceHistory.map((item, index) => {
                    const finalScore = computeScores(item.result).finalScore;
                    return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {formatDate(item.timestamp)}
                          {index === 0 && (
                            <Chip 
                              label="MOST RECENT" 
                              size="small" 
                              color="info" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.65rem', 
                                fontWeight: 'bold',
                                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                color: 'white'
                              }} 
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                        {item.provider}
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold" color={
                          item.scores.rating === 'GREEN' ? 'success.main' : 
                          item.scores.rating === 'YELLOW' ? 'warning.main' : 'error.main'
                        }>
                          {finalScore}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.scores.rating === 'YELLOW' ? 'ORANGE' : item.scores.rating}
                          size="small"
                          variant="outlined"
                          color={
                            item.scores.rating === 'GREEN' ? 'success' : 
                            item.scores.rating === 'YELLOW' ? 'warning' : 'error'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton
                              color="primary"
                              onClick={() => navigate(`/assessment/${item.id}`)}
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Record">
                            <IconButton
                              color="error"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this assessment record?')) {
                                  removeAssessment(item.id);
                                }
                              }}
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Container>
  );
}
